import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Trophy, ArrowLeft, GameController, ChartLineUp, Eye, Users
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const LEAGUE_TYPES = [
  { value: 'casual', label: 'Casual — relaxed competitive play' },
  { value: 'competitive', label: 'Competitive — serious ranked play' },
  { value: 'ladder', label: 'Ladder — ongoing challenge system' },
  { value: 'team', label: 'Team — team-based competition' },
  { value: 'shop_league', label: 'Shop League — hosted by a game shop' },
];

const SCORING_SYSTEMS = [
  { value: 'points', label: 'Points — win/loss/draw/attendance points' },
  { value: 'wins_only', label: 'Wins Only — simple win count' },
  { value: 'elo_weighted', label: 'ELO Weighted — rating-based' },
  { value: 'custom', label: 'Custom — define your own rules' },
];

const VISIBILITIES = [
  { value: 'public', label: 'Public — anyone can find and join' },
  { value: 'unlisted', label: 'Unlisted — join via link only' },
  { value: 'private', label: 'Private — invite required' },
];

export default function CreateLeaguePage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', game_id: '', league_type: 'casual',
    visibility: 'public', scoring_system_type: 'points',
    standings_visibility: 'public', season_required: true,
  });

  const set = (k, v) => setForm({ ...form, [k]: v });
  const inputCls = "w-full bg-[#0c0c14] border border-white/[0.08] rounded-lg px-3 py-2.5 text-[13px] text-white focus:border-[#F59E0B]/40 focus:outline-none placeholder-[#2a2a2a]";
  const labelCls = "text-[11px] text-[#555] uppercase tracking-wider font-medium mb-1.5 block";
  const selectCls = inputCls;

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('League name is required'); return; }
    if (!form.game_id.trim()) { toast.error('Game is required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/leagues`, form, { withCredentials: true });
      toast.success('League created!');
      navigate('/leagues');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create league');
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="create-league-page">
      <button onClick={() => navigate('/leagues')} className="flex items-center gap-1 text-[12px] text-[#555] hover:text-white mb-4 transition-colors">
        <ArrowLeft size={14} /> Back to Leagues
      </button>

      <div className="mb-5">
        <h1 className="text-xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Create League</h1>
        <p className="text-[13px] text-[#444] mt-0.5">Organize competitive play with seasons and standings</p>
      </div>

      <div className="space-y-5">
        {/* Basic Info */}
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
          <h2 className="text-[14px] font-bold text-white mb-4 flex items-center gap-2">
            <Trophy size={16} weight="duotone" className="text-[#F59E0B]" /> League Details
          </h2>
          <div className="space-y-3">
            <div>
              <label className={labelCls}>League Name *</label>
              <input className={inputCls} value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="e.g., Portland Catan League" data-testid="input-name" />
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea className={inputCls + " h-20 resize-none"} value={form.description}
                onChange={e => set('description', e.target.value)}
                placeholder="What's this league about..." data-testid="input-description" />
            </div>
            <div>
              <label className={labelCls}>Game *</label>
              <input className={inputCls} value={form.game_id} onChange={e => set('game_id', e.target.value)}
                placeholder="e.g., Catan, Chess, Magic: The Gathering" data-testid="input-game" />
            </div>
          </div>
        </div>

        {/* Configuration */}
        <div className="bg-[#0c0c14] border border-white/[0.06] rounded-xl p-5">
          <h2 className="text-[14px] font-bold text-white mb-4 flex items-center gap-2">
            <ChartLineUp size={16} weight="duotone" className="text-[#3B82F6]" /> Configuration
          </h2>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>League Type</label>
                <select className={selectCls} value={form.league_type} onChange={e => set('league_type', e.target.value)} data-testid="select-type">
                  {LEAGUE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Scoring System</label>
                <select className={selectCls} value={form.scoring_system_type} onChange={e => set('scoring_system_type', e.target.value)} data-testid="select-scoring">
                  {SCORING_SYSTEMS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Visibility</label>
                <select className={selectCls} value={form.visibility} onChange={e => set('visibility', e.target.value)} data-testid="select-visibility">
                  {VISIBILITIES.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Standings Visibility</label>
                <select className={selectCls} value={form.standings_visibility} onChange={e => set('standings_visibility', e.target.value)} data-testid="select-standings-vis">
                  <option value="public">Public — anyone can see</option>
                  <option value="members_only">Members Only</option>
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.season_required} onChange={e => set('season_required', e.target.checked)}
                className="w-4 h-4 rounded border-white/[0.2] bg-[#07070c]" data-testid="check-seasons" />
              <span className="text-[13px] text-white">Require Seasons</span>
              <span className="text-[10px] text-[#555]">(recommended for organized play)</span>
            </label>
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-[#F59E0B]/5 border border-[#F59E0B]/15 rounded-xl p-4">
          <p className="text-[12px] text-[#F59E0B] font-medium mb-1">After creating your league:</p>
          <ul className="text-[11px] text-[#888] space-y-0.5 list-disc list-inside">
            <li>Create a season with start/end dates</li>
            <li>Invite members to join</li>
            <li>Record matches to update standings automatically</li>
            <li>Customize scoring rules (win/loss/draw points)</li>
          </ul>
        </div>

        {/* Submit */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/leagues')}
            className="flex-1 h-10 bg-transparent border-white/[0.08] text-[#888] hover:text-white">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting}
            className="flex-1 h-10 bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold"
            data-testid="submit-league-btn">
            {submitting ? 'Creating...' : 'Create League'}
          </Button>
        </div>
      </div>
    </div>
  );
}
